import React, { useEffect, useState } from "react";
import { FiChevronDown, FiSearch, FiStar } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import CandlestickChart from "../charts/CandlestickChart";
import Footer from "../components/Footer";
import Header from "../components/Header";
import CoinIcon from "../components/CoinIcon";
import Get from "../services/Get";
import Post from "../services/Post";
import { useBinanceTicker, useBinanceTickers } from "../services/BinanceTickerContext";
import { marketAsset, marketChange, marketName, marketNumber, marketPrice, marketSymbol, useMarketAssets } from "../services/useMarketAssets";
import "./TradingTerminal.css";

const intervals = ["1m", "5m", "15m", "1h", "4h", "1d", "1w"];
const OPEN_STATUSES = ["pending", "new", "open", "partially_filled"];
const brokerRows = (response, marketId) => (Array.isArray(response?.data) ? response.data : []).filter((order) => String(order.market_id) === String(marketId));
const orderTabs = ["Open Orders", "Order History", "Trade History", "Funds"];
const normalizeSymbol = (value) => String(value ?? "").replace(/[^a-z0-9]/gi, "").replace(/USDT$/i, "").toUpperCase();
const numberText = (value, digits = 8) => {
  const number = marketNumber(value);
  return number == null ? "—" : number.toLocaleString("en-US", { maximumFractionDigits: digits });
};
const timeText = (value) => {
  if (!value) return "—";
  const dateValue = typeof value === "number" || /^\d+$/.test(String(value)) ? Number(value) : value;
  return new Date(dateValue).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};
const normalizeBookRows = (rows = []) => {
  let total = 0;
  return rows.map((row) => {
    const price = Number(Array.isArray(row) ? row[0] : row.price);
    const remaining = Math.max(Number(Array.isArray(row) ? row[1] : row.quantity) - Number(row.filled_quantity ?? 0), 0);
    total += price * remaining;
    return { price, quantity: remaining, total };
  }).filter((row) => Number.isFinite(row.price) && Number.isFinite(row.quantity) && row.quantity > 0);
};

async function fetchTerminalMarketData(marketId, pairSymbol) {
  const [bookResult, tradeResult] = await Promise.allSettled([Get.OrderBook(marketId), Get.MarketTrades(marketId)]);
  const internalBook = bookResult.status === "fulfilled" ? bookResult.value : {};
  const book = {
    asks: normalizeBookRows(internalBook.asks),
    bids: normalizeBookRows(internalBook.bids),
  };
  const tradeResponse = tradeResult.status === "fulfilled" ? tradeResult.value : [];
  let trades = Array.isArray(tradeResponse) ? tradeResponse : Array.isArray(tradeResponse?.data) ? tradeResponse.data : [];

  if (book.asks.length === 0 && book.bids.length === 0) {
    try {
      const depth = await Get.BinanceDepth(pairSymbol);
      book.asks = normalizeBookRows(depth.asks);
      book.bids = normalizeBookRows(depth.bids);
    } catch {
      book.asks = [];
      book.bids = [];
    }
  }

  if (trades.length === 0) {
    try {
      trades = await Get.BinanceMarketTrades(pairSymbol);
    } catch {
      trades = [];
    }
  }

  return { book, trades };
}

function AssetBadge({ market, size = "normal" }) {
  const asset = marketAsset(market);
  const symbol = marketSymbol(market);
  return <CoinIcon className={`terminal-asset-badge terminal-asset-badge-${size}`} symbol={symbol} logo={asset.logo} name={marketName(market)} size={size} />;
}

function BookRows({ rows, side, onPriceClick, symbol }) {
  if (!rows.length) return <div className="terminal-empty-row">No open {side === "asks" ? "sell" : "buy"} orders</div>;
  return rows.map((row, index) => (
    <button className={`terminal-book-row terminal-${side}`} key={`${row.price}-${index}`} type="button" onClick={() => onPriceClick(String(row.price))}>
      <span>{numberText(row.price)}</span><span>{numberText(row.quantity)}</span><span>{numberText(row.total)}</span>
    </button>
  ));
}

const isStep = (value, base, step) => {
  const stepNumber = Number(step);
  if (!(stepNumber > 0)) return true;
  const ratio = (value - Number(base)) / stepNumber;
  return Math.abs(ratio - Math.round(ratio)) < 1e-8;
};

function checkRules(rules, { type, side, price, quantity, total, quoteSymbol, lastPrice }) {
  if (!rules) return null;
  if (!rules.tradable) return { field: "form", text: "This market is not open for trading right now." };
  const minQty = Number(type === "market" ? rules.market_min_qty : rules.min_qty);
  const step = type === "market" ? rules.market_step_size : rules.step_size;
  if (quantity < minQty) return { field: "quantity", text: `Minimum amount is ${minQty}.` };
  if (!isStep(quantity, minQty, step)) return { field: "quantity", text: `Amount must be in steps of ${Number(step)}.` };
  if (type === "limit" && !isStep(price, rules.min_price, rules.tick_size)) return { field: "price", text: `Price must be in steps of ${Number(rules.tick_size)}.` };
  if (type === "limit" && rules.percent && lastPrice > 0) {
    const up = Number(side === "buy" ? rules.percent.bid_up : rules.percent.ask_up);
    const down = Number(side === "buy" ? rules.percent.bid_down : rules.percent.ask_down);
    if (price > lastPrice * up || price < lastPrice * down) return { field: "price", text: `Price must be within ${((down - 1) * 100).toFixed(0)}% to +${((up - 1) * 100).toFixed(0)}% of the current market price.` };
  }
  if (total < Number(rules.min_notional) && (type === "limit" || rules.apply_min_to_market)) return { field: "total", text: `Minimum order value is ${Number(rules.min_notional)} ${quoteSymbol}.` };
  return null;
}

function OrderEntry({ side, market, ticker, signedIn, availableBalance, onPlaced }) {
  const [type, setType] = useState("limit");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [rules, setRules] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [totalInput, setTotalInput] = useState(null);
  const baseSymbol = marketSymbol(market);
  const quoteSymbol = market?.quote_asset ?? "USDT";
  const effectivePrice = type === "market" ? ticker?.price : (price === "" ? ticker?.price : Number(price));
  const total = Number.isFinite(effectivePrice) && Number(quantity) > 0 ? effectivePrice * Number(quantity) : 0;
  const available = marketNumber(availableBalance?.available);
  const maxQuantity = available == null || !effectivePrice || effectivePrice <= 0 ? 0 : (side === "buy" ? available / effectivePrice : available);
  const amountPercent = maxQuantity > 0 ? Math.min(100, Number(quantity || 0) / maxQuantity * 100) : 0;

  useEffect(() => {
    setPrice("");
    setQuantity("");
    setMessage("");
    setFieldErrors({});
  }, [market?.id]);

  const changeTotal = (value) => {
    setTotalInput(value);
    const amount = Number(value);
    if (!(amount > 0) || !(Number(effectivePrice) > 0)) {
      setQuantity("");
      return;
    }
    const step = Number(rules?.step_size);
    const raw = amount / Number(effectivePrice);
    const quantityValue = step > 0 ? Math.ceil(raw / step - 1e-9) * step : raw;
    setQuantity(String(Number(quantityValue.toFixed(8))));
  };

  useEffect(() => {
    let active = true;
    setRules(null);
    if (!baseSymbol) return () => { active = false; };
    Get.BinanceRules(`${baseSymbol}${quoteSymbol}`)
      .then((data) => { if (active) setRules(data); })
      .catch(() => { if (active) setRules(null); });
    return () => { active = false; };
  }, [baseSymbol, quoteSymbol]);

  useEffect(() => {
    const applyBookPrice = (event) => setPrice(String(event.detail ?? ""));
    window.addEventListener("terminal-price-select", applyBookPrice);
    return () => window.removeEventListener("terminal-price-select", applyBookPrice);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setMessage("");
    setFieldErrors({});
    if (!signedIn) {
      setMessage("Log in to place an order.");
      return;
    }
    if (!market?.id || !Number.isFinite(Number(effectivePrice)) || Number(effectivePrice) <= 0 || Number(quantity) <= 0) {
      setMessage("Enter a valid price and amount.");
      return;
    }

    const ruleError = checkRules(rules, { type, side, lastPrice: ticker?.price, price: Number(effectivePrice), quantity: Number(quantity), total, quoteSymbol });
    if (ruleError) {
      if (ruleError.field === "form") setMessage(ruleError.text);
      else setFieldErrors({ [ruleError.field]: ruleError.text });
      return;
    }

    setSubmitting(true);
    try {
      const payload = { market_id: market.id, side, type };
      if (type === "limit") {
        payload.price = String(effectivePrice);
        payload.quantity = String(quantity);
      } else if (side === "buy") {
        payload.quote_quantity = String(total);
      } else {
        payload.quantity = String(quantity);
      }
      const response = await Post.CreateBrokerOrder(payload);
      if (!response?.order) {
        if (response?.errors) {
          const mapped = {};
          Object.entries(response.errors).forEach(([key, messages]) => {
            const field = key === "price" ? "price" : key === "quote_quantity" ? "total" : key === "quantity" ? (String(messages[0]).includes("value") ? "total" : "quantity") : "form";
            mapped[field] = messages[0];
          });
          if (!mapped.form) {
            setFieldErrors(mapped);
            return;
          }
        }
        throw new Error(response?.message || response?.error || "Order could not be placed.");
      }
      setMessage("Order placed on Binance.");
      setQuantity("");
      onPlaced();
    } catch (error) {
      setMessage(error.message || "Order could not be placed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={`terminal-order-form terminal-order-form-${side}`} onSubmit={submit}>
      <div className="terminal-order-form-head">
        <h3>{side === "buy" ? "Buy" : "Sell"} {baseSymbol}</h3>
        <span>Available <b>{available == null ? "—" : numberText(available)} {side === "buy" ? quoteSymbol : baseSymbol}</b></span>
      </div>
      <div className="terminal-order-types" role="group" aria-label={`${side} order type`}>
        {["limit", "market"].map((option) => <button key={option} type="button" className={type === option ? "is-active" : ""} onClick={() => setType(option)}>{option === "limit" ? "Limit" : "Market"}</button>)}
        <button type="button" disabled title="Stop orders are not enabled for this market">Stop Limit</button>
      </div>
      <label className="terminal-field">
        <span>Price</span>
        <div><input value={type === "market" ? (ticker?.price ?? "") : (price || ticker?.price || "")} onChange={(event) => setPrice(event.target.value)} readOnly={type === "market"} inputMode="decimal" aria-label={`${side} price`} placeholder="Market price unavailable" /><small>{quoteSymbol}</small></div>
        {fieldErrors.price && <em className="terminal-field-error">{fieldErrors.price}</em>}
      </label>
      <label className="terminal-field">
        <span>Amount</span>
        <div><input value={quantity} onChange={(event) => setQuantity(event.target.value)} inputMode="decimal" aria-label={`${side} amount`} placeholder="0.00" /><small>{baseSymbol}</small></div>
        {fieldErrors.quantity && <em className="terminal-field-error">{fieldErrors.quantity}</em>}
      </label>
      <label className="terminal-size-track" aria-label={`${side} amount percentage`}>
        <input type="range" min="0" max="100" step="1" value={amountPercent} disabled={maxQuantity <= 0} onChange={(event) => setQuantity(String(maxQuantity * Number(event.target.value) / 100))} />
      </label>
      <label className="terminal-field">
        <span>Total</span>
        <div><input value={totalInput ?? (total ? String(Number(total.toFixed(8))) : "")} onChange={(event) => changeTotal(event.target.value)} onBlur={() => setTotalInput(null)} inputMode="decimal" aria-label={`${side} total`} placeholder="0.00" /><small>{quoteSymbol}</small></div>
        {fieldErrors.total && <em className="terminal-field-error">{fieldErrors.total}</em>}
      </label>
      <label className="terminal-checkbox"><input type="checkbox" disabled /> TP/SL</label>
      {rules && <p className="terminal-form-hint">Min order {Number(rules.min_notional)} {quoteSymbol} · Min amount {Number(rules.min_qty)} {baseSymbol}</p>}
      <p className={`terminal-form-message ${message && !message.includes("placed") ? "is-error" : ""}`} role={message ? "status" : undefined}>{message || ""}</p>
      <button className={`terminal-submit terminal-submit-${side}`} type="submit" disabled={submitting || !market?.id}>
        {submitting ? "Submitting…" : `${side === "buy" ? "Buy" : "Sell"} ${baseSymbol || "Asset"}`}
      </button>
    </form>
  );
}

function TradingTerminal() {
  const [searchParams] = useSearchParams();
  const { assets, loading: marketsLoading, error: marketsError } = useMarketAssets();
  const requestedSymbol = normalizeSymbol(searchParams.get("symbol"));
  const market = assets.find((item) => marketSymbol(item) === requestedSymbol) ?? assets[0];
  const symbol = marketSymbol(market);
  const pairSymbol = market?.market_symbol ?? `${symbol}${market?.quote_asset ?? "USDT"}`;
  const ticker = useBinanceTicker(symbol);
  const tickers = useBinanceTickers();
  const [interval, setInterval] = useState("1h");
  const [chartView, setChartView] = useState("Chart");
  const [book, setBook] = useState({ asks: [], bids: [] });
  const [trades, setTrades] = useState([]);
  const [watchSearch, setWatchSearch] = useState("");
  const [quoteFilter, setQuoteFilter] = useState("all");
  const [orderTab, setOrderTab] = useState("Open Orders");
  const [orders, setOrders] = useState([]);
  const [myTrades, setMyTrades] = useState([]);
  const [balances, setBalances] = useState([]);
  const [orderMessage, setOrderMessage] = useState("");
  const [signedIn] = useState(() => Boolean(localStorage.getItem("token")));
  const quoteSymbol = market?.quote_asset ?? "USDT";
  const change = marketChange(ticker);

  useEffect(() => {
    if (!symbol) return;
    const priceText = ticker?.price == null ? "" : `${marketPrice(ticker)} `;
    document.title = `${priceText}${symbol}/${quoteSymbol} | TillionX`;
  }, [symbol, quoteSymbol, ticker]);

  const loadMarketData = async () => {
    if (!market?.id) {
      setBook({ asks: [], bids: [] });
      setTrades([]);
      return;
    }
    const marketData = await fetchTerminalMarketData(market.id, pairSymbol);
    setBook(marketData.book);
    setTrades(marketData.trades);
  };

  const loadOrders = async () => {
    if (!signedIn || !market?.id) {
      setOrders([]);
      return;
    }
    const response = await Get.BrokerOrders({ per_page: 100 });
    setOrders(brokerRows(response, market.id));
  };

  const loadBalances = async () => {
    if (!signedIn) {
      setBalances([]);
      return;
    }
    const response = await Get.Wallets();
    setBalances(Array.isArray(response?.balances) ? response.balances : []);
  };

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      if (!market?.id) return;
      const marketData = await fetchTerminalMarketData(market.id, pairSymbol);
      if (!active) return;
      setBook(marketData.book);
      setTrades(marketData.trades);
    };
    refresh();
    const timer = window.setInterval(refresh, 5000);
    return () => { active = false; window.clearInterval(timer); };
  }, [market?.id, pairSymbol]);

  useEffect(() => {
    let active = true;
    if (!signedIn || !market?.id) {
      setOrders([]);
      setMyTrades([]);
      setBalances([]);
      return () => { active = false; };
    }
    Get.Wallets().then((response) => {
      if (active) setBalances(Array.isArray(response?.balances) ? response.balances : []);
    }).catch(() => { if (active) setBalances([]); });
    Get.BrokerOrders({ per_page: 100 }).then((response) => {
      if (active) setOrders(brokerRows(response, market.id));
    }).catch(() => { if (active) setOrders([]); });
    Get.MyTrades({ market_id: market.id }).then((response) => {
      const rows = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
      if (active) setMyTrades(rows);
    }).catch(() => { if (active) setMyTrades([]); });
    return () => { active = false; };
  }, [market?.id, signedIn]);

  const quoteOptions = [...new Set(assets.map((item) => item.quote_asset).filter(Boolean))];
  const visibleMarkets = assets.filter((item) => {
    const matchesQuote = quoteFilter === "all" || item.quote_asset === quoteFilter;
    const matchesSearch = `${marketSymbol(item)} ${marketName(item)}`.toLowerCase().includes(watchSearch.toLowerCase());
    return matchesQuote && matchesSearch;
  });
  const asks = book.asks.slice(0, 12);
  const bids = book.bids.slice(0, 12);
  const openOrders = orders.filter((order) => OPEN_STATUSES.includes(order.status));
  const historicalOrders = orders.filter((order) => !OPEN_STATUSES.includes(order.status));
  const selectPrice = (price) => window.dispatchEvent(new CustomEvent("terminal-price-select", { detail: price }));
  const balanceFor = (assetSymbol) => balances.find((balance) => String(balance.asset?.symbol ?? "").toUpperCase() === String(assetSymbol ?? "").toUpperCase());

  const cancelOrder = async (orderId) => {
    setOrderMessage("");
    try {
      const response = await Post.CancelBrokerOrder(orderId);
      if (!response?.order) throw new Error(response?.message || response?.error || "Order could not be cancelled.");
      setOrderMessage("Order cancelled.");
      loadOrders();
      loadMarketData();
      loadBalances();
    } catch (error) {
      setOrderMessage(error.message || "Order could not be cancelled.");
    }
  };

  return (
    <div className="trading-terminal-page">
      <Header />
      <div className="terminal-pair-strip">
        <div className="terminal-pair-title">
          <AssetBadge market={market} />
          <div><strong>{symbol || (marketsLoading ? "Loading markets" : "No market")}/{quoteSymbol}</strong><small>{marketName(market)} · Spot</small></div>
          <FiChevronDown aria-hidden="true" />
        </div>
        <div className="terminal-current-price"><strong>{marketPrice(ticker)}</strong><span className={change.startsWith("+") ? "terminal-up" : change === "—" ? "" : "terminal-down"}>{change}</span></div>
        <div className="terminal-pair-stat"><small>24h High</small><b>{numberText(ticker?.high, 2)}</b></div>
        <div className="terminal-pair-stat"><small>24h Low</small><b>{numberText(ticker?.low, 2)}</b></div>
        <div className="terminal-pair-stat"><small>24h Volume ({symbol})</small><b>{numberText(ticker?.volume, 2)}</b></div>
        <div className="terminal-pair-stat terminal-pair-stat-cap"><small>24h Quote Volume</small><b>{ticker?.quoteVolume == null ? "—" : numberText(ticker.quoteVolume, 2)}</b></div>
        <button className="terminal-star" type="button" title="Add to favorites" aria-label="Add to favorites"><FiStar /></button>
      </div>

      <main className="terminal-workspace">
        <aside className="terminal-panel terminal-orderbook-panel">
          <div className="terminal-panel-heading"><h2>Order Book</h2><button type="button" aria-label="Order book settings"><FiChevronDown /></button></div>
          <div className="terminal-book-columns"><span>Price ({quoteSymbol})</span><span>Amount ({symbol})</span><span>Total</span></div>
          <div className="terminal-book-list terminal-ask-list"><BookRows rows={asks} side="asks" onPriceClick={selectPrice} symbol={symbol} /></div>
          <div className={`terminal-book-mid ${change.startsWith("+") ? "terminal-up" : "terminal-down"}`}><strong>{marketPrice(ticker)}</strong><span>{ticker?.change == null ? "" : `${ticker.change > 0 ? "+" : ""}${ticker.change.toFixed(2)}%`}</span></div>
          <div className="terminal-book-list terminal-bid-list"><BookRows rows={bids} side="bids" onPriceClick={selectPrice} symbol={symbol} /></div>
          <div className="terminal-book-footer"><span>Spread</span><b>{asks[0]?.price && bids[0]?.price ? numberText(Number(asks[0].price) - Number(bids[0].price), 8) : "—"}</b></div>
        </aside>

        <section className="terminal-center-column">
          <section className={`terminal-panel terminal-chart-panel ${chartView === "Chart" ? "" : "terminal-chart-panel-details"}`}>
            <div className="terminal-tabs terminal-chart-tabs" role="tablist" aria-label="Market information">
              {["Chart", "Info", "Trading Data"].map((tab) => <button key={tab} type="button" role="tab" aria-selected={chartView === tab} className={chartView === tab ? "is-active" : ""} onClick={() => setChartView(tab)}>{tab}</button>)}
              <span className="terminal-chart-market-label">{pairSymbol}</span>
            </div>
            {chartView === "Chart" && <>
              <div className="terminal-chart-toolbar">
                <div className="terminal-intervals" aria-label="Candlestick interval">
                  {intervals.map((value) => <button type="button" key={value} className={interval === value ? "is-active" : ""} onClick={() => setInterval(value)}>{value}</button>)}
                </div>
                <div className="terminal-chart-modes"><button type="button" className="is-active">Original</button><button type="button" disabled title="TradingView chart is not configured">TradingView</button><button type="button" disabled title="Depth chart is not configured">Depth</button></div>
              </div>
              <CandlestickChart symbol={market?.symbol ?? ""} interval={interval} />
            </>}
            {chartView === "Info" && <div className="terminal-info-grid">
              <div><small>Category</small><b>{market?.category || "—"}</b></div>
              <div><small>Network</small><b>{market?.networks?.map((item) => item.name ?? item.symbol).join(", ") || "—"}</b></div>
              <div><small>Total Supply</small><b>{numberText(market?.total_supply)}</b></div>
              <div><small>Unlocked</small><b>{numberText(market?.total_unlocked)}</b></div>
              <div><small>Next Unlock</small><b>{numberText(market?.next_unlock_amount)}</b></div>
              <div><small>Next Unlock Date</small><b>{market?.next_unlock_at ? new Date(market.next_unlock_at).toLocaleDateString() : "—"}</b></div>
            </div>}
            {chartView === "Trading Data" && <div className="terminal-info-grid">
              <div><small>24h High</small><b>{numberText(ticker?.high, 2)}</b></div>
              <div><small>24h Low</small><b>{numberText(ticker?.low, 2)}</b></div>
              <div><small>24h Volume</small><b>{numberText(ticker?.volume, 2)} {symbol}</b></div>
              <div><small>Quote Volume</small><b>{ticker?.quoteVolume == null ? "—" : `${numberText(ticker.quoteVolume, 2)} ${quoteSymbol}`}</b></div>
            </div>}
          </section>

          <section className="terminal-panel terminal-entry-panel">
            <div className="terminal-entry-heading"><div className="terminal-tabs" role="tablist" aria-label="Trading product"><button className="is-active" type="button">Spot</button><button disabled type="button" title="Margin trading is not enabled">Cross</button><button disabled type="button" title="Margin trading is not enabled">Isolated</button><button disabled type="button" title="Grid trading is not enabled">Grid</button></div><Link to="/wallet">Wallet</Link></div>
            <div className="terminal-order-entry-grid">
              <OrderEntry side="buy" market={market} ticker={ticker} signedIn={signedIn} availableBalance={balanceFor(quoteSymbol)} onPlaced={() => { loadOrders(); loadMarketData(); loadBalances(); }} />
              <OrderEntry side="sell" market={market} ticker={ticker} signedIn={signedIn} availableBalance={balanceFor(symbol)} onPlaced={() => { loadOrders(); loadMarketData(); loadBalances(); }} />
            </div>
          </section>
        </section>

        <aside className="terminal-right-column">
          <section className="terminal-panel terminal-marketlist-panel">
            <label className="terminal-market-search"><FiSearch aria-hidden="true" /><input value={watchSearch} onChange={(event) => setWatchSearch(event.target.value)} placeholder="Search" aria-label="Search markets" /></label>
            <div className="terminal-quote-filters"><button type="button" className={quoteFilter === "all" ? "is-active" : ""} onClick={() => setQuoteFilter("all")}>All</button>{quoteOptions.map((quote) => <button type="button" className={quoteFilter === quote ? "is-active" : ""} key={quote} onClick={() => setQuoteFilter(quote)}>{quote}</button>)}</div>
            <div className="terminal-marketlist-header"><span>Pair</span><span>Price / 24h</span></div>
            <div className="terminal-marketlist-rows">
              {visibleMarkets.map((row) => {
                const rowSymbol = marketSymbol(row);
                const rowTicker = tickers[rowSymbol];
                const rowChange = marketChange(rowTicker);
                return <Link key={rowSymbol} className={`terminal-marketlist-row ${rowSymbol === symbol ? "is-selected" : ""}`} to={`/trade?symbol=${encodeURIComponent(row.market_symbol ?? row.symbol ?? `${rowSymbol}USDT`)}`}>
                  <span className="terminal-marketlist-pair"><AssetBadge market={row} size="small" /><b>{rowSymbol}</b></span>
                  <span><b>{marketPrice(rowTicker)}</b><small className={rowChange.startsWith("+") ? "terminal-up" : rowChange === "—" ? "" : "terminal-down"}>{rowChange}</small></span>
                </Link>;
              })}
              {!marketsLoading && !marketsError && visibleMarkets.length === 0 && <p className="terminal-empty-row">No matching markets</p>}
              {marketsError && <p className="terminal-empty-row">{marketsError}</p>}
            </div>
          </section>
          <section className="terminal-panel terminal-trades-panel">
            <div className="terminal-panel-heading"><h2>Market Trades</h2><span>Live feed</span></div>
            <div className="terminal-trades-columns"><span>Price ({quoteSymbol})</span><span>Amount ({symbol})</span><span>Time</span></div>
            <div className="terminal-trade-list">
              {trades.slice(0, 18).map((trade, index) => <div className="terminal-trade-row" key={trade.id ?? `${trade.price}-${index}`}>
                <span className={typeof trade.isBuyerMaker === "boolean" ? (trade.isBuyerMaker ? "terminal-down" : "terminal-up") : Number(trade.price) >= (ticker?.price ?? 0) ? "terminal-up" : "terminal-down"}>{numberText(trade.price)}</span><span>{numberText(trade.quantity ?? trade.qty)}</span><span>{timeText(trade.executed_at ?? trade.created_at ?? trade.time)}</span>
              </div>)}
              {trades.length === 0 && <p className="terminal-empty-row">No recent trades</p>}
            </div>
          </section>
        </aside>
      </main>

      <section className="terminal-panel terminal-orders-panel">
        <div className="terminal-orders-header"><div className="terminal-tabs" role="tablist" aria-label="Account orders">{orderTabs.map((tab) => <button key={tab} type="button" role="tab" aria-selected={orderTab === tab} className={orderTab === tab ? "is-active" : ""} onClick={() => setOrderTab(tab)}>{tab}</button>)}</div><span>{orderMessage}</span></div>
        {orderTab === "Open Orders" && <div className="terminal-order-table">
          <div className="terminal-order-table-head"><span>Date</span><span>Pair</span><span>Type</span><span>Side</span><span>Price</span><span>Amount</span><span>Filled</span><span>Status</span><span>Action</span></div>
          {openOrders.map((order) => <div className="terminal-order-table-row" key={order.id}><span>{timeText(order.placed_at ?? order.created_at)}</span><span>{order.market?.symbol ?? pairSymbol}</span><span>{order.type}</span><span className={order.side === "buy" ? "terminal-up" : "terminal-down"}>{order.side}</span><span>{numberText(order.price)}</span><span>{numberText(order.quantity)}</span><span>{numberText(order.filled_quantity)}</span><span>{order.status}</span><button type="button" onClick={() => cancelOrder(order.id)}>Cancel</button></div>)}
          {openOrders.length === 0 && <div className="terminal-orders-empty">{signedIn ? "You have no open orders." : <Link to="/login">Log in to view your orders.</Link>}</div>}
        </div>}
        {orderTab === "Order History" && <div className="terminal-order-table">
          <div className="terminal-order-table-head"><span>Date</span><span>Pair</span><span>Type</span><span>Side</span><span>Price</span><span>Amount</span><span>Filled</span><span>Status</span><span /></div>
          {historicalOrders.map((order) => <div className="terminal-order-table-row" key={order.id}><span>{timeText(order.placed_at ?? order.created_at)}</span><span>{order.market?.symbol ?? pairSymbol}</span><span>{order.type}</span><span className={order.side === "buy" ? "terminal-up" : "terminal-down"}>{order.side}</span><span>{numberText(order.price)}</span><span>{numberText(order.quantity)}</span><span>{numberText(order.filled_quantity)}</span><span>{order.status}</span><span /></div>)}
          {historicalOrders.length === 0 && <div className="terminal-orders-empty">No order history for this pair.</div>}
        </div>}
        {orderTab === "Trade History" && <div className="terminal-order-table">
          <div className="terminal-order-table-head terminal-trade-history-head"><span>Date</span><span>Pair</span><span>Price</span><span>Quantity</span><span>Role</span></div>
          {myTrades.map((trade, index) => <div className="terminal-order-table-row terminal-trade-history-row" key={trade.id ?? index}><span>{timeText(trade.executed_at)}</span><span>{pairSymbol}</span><span>{numberText(trade.price)}</span><span>{numberText(trade.quantity)}</span><span>{trade.maker_id ? "Maker" : "Taker"}</span></div>)}
          {myTrades.length === 0 && <div className="terminal-orders-empty">No trade history for this pair.</div>}
        </div>}
        {orderTab === "Funds" && <div className="terminal-funds-empty">Balances are available in your <Link to="/wallet">wallet</Link>.</div>}
      </section>
      <Footer />
    </div>
  );
}

export default TradingTerminal;